import { z } from 'zod';

import {
  BOROUGHS,
  BUILDING_DOCUMENT_CATEGORIES,
  GEOCODE_SOURCES,
  HEAT_SOURCES,
  HOT_WATER_SOURCES,
  LEAD_STATUSES,
  OCCUPANCY_STATUSES,
  STOVE_FUELS,
  UNIT_TYPES,
} from './constants.js';
import { cursorPaginationQuery } from './pagination.js';

const optionalCaption = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

/** A US state two-letter code. We're not policing the actual list here —
 *  validation against ISO is overkill — but we do force two ASCII letters. */
export const stateCodeSchema = z
  .string()
  .trim()
  .length(2, 'Use a 2-letter state code')
  .regex(/^[A-Za-z]{2}$/, 'Use a 2-letter state code')
  .transform((v) => v.toUpperCase());

/** 5- or 9-digit US postal code. The hyphen in ZIP+4 is optional. */
export const postalCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{5}(?:-?\d{4})?$/, 'Use a 5- or 9-digit ZIP code');

/** NYC BIN is exactly 7 digits. */
export const binSchema = z
  .string()
  .trim()
  .regex(/^\d{7}$/, 'BIN must be exactly 7 digits');

/** NYC BBL is exactly 10 digits (1-digit borough + 5-digit block + 4-digit lot). */
export const bblSchema = z
  .string()
  .trim()
  .regex(/^\d{10}$/, 'BBL must be exactly 10 digits');

export const boroughSchema = z.enum(BOROUGHS);
export const unitTypeSchema = z.enum(UNIT_TYPES);
export const occupancyStatusSchema = z.enum(OCCUPANCY_STATUSES);
export const geocodeSourceSchema = z.enum(GEOCODE_SOURCES);
export const heatSourceSchema = z.enum(HEAT_SOURCES);
export const hotWaterSourceSchema = z.enum(HOT_WATER_SOURCES);
export const stoveFuelSchema = z.enum(STOVE_FUELS);
export const leadStatusSchema = z.enum(LEAD_STATUSES);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

const buildingMetadata = z
  .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .optional()
  .default({});

const contactInput = z.object({
  name: z.string().trim().min(1).max(120),
  roleLabel: z.string().trim().min(1).max(60),
  phone: optionalText(40),
  email: z.string().trim().toLowerCase().email().max(254).nullish().transform((v) => v ?? null),
  notes: optionalText(2_000),
});

/** Shared fields used by both create & update. Address fields are required
 *  on create, optional on update. */
const addressFields = {
  addressLine1: z.string().trim().min(1).max(200),
  addressLine2: optionalText(200),
  city: z.string().trim().min(1).max(120),
  state: stateCodeSchema,
  postalCode: postalCodeSchema,
};

export const createBuildingInput = z
  .object({
    name: z.string().trim().min(1).max(200),
    ...addressFields,
    bin: binSchema.nullish().transform((v) => v ?? null),
    bbl: bblSchema.nullish().transform((v) => v ?? null),
    unitCountOverride: z
      .number()
      .int()
      .nonnegative()
      .max(10_000)
      .nullish()
      .transform((v) => v ?? null),
    notes: optionalText(4_000),
    metadata: buildingMetadata,
    contacts: z.array(contactInput).max(20).optional().default([]),
  })
  .superRefine((value, ctx) => {
    // Manual override: if one of bin/bbl is supplied, both should be —
    // they're a pair from the City's perspective. Allow neither, but not
    // half a pair.
    if ((value.bin && !value.bbl) || (!value.bin && value.bbl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bbl'],
        message: 'Provide both BIN and BBL when setting them manually',
      });
    }
  });
export type CreateBuildingInput = z.infer<typeof createBuildingInput>;

export const updateBuildingInput = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    addressLine1: z.string().trim().min(1).max(200).optional(),
    addressLine2: optionalText(200).optional(),
    city: z.string().trim().min(1).max(120).optional(),
    state: stateCodeSchema.optional(),
    postalCode: postalCodeSchema.optional(),
    bin: binSchema.nullish().transform((v) => v ?? null).optional(),
    bbl: bblSchema.nullish().transform((v) => v ?? null).optional(),
    unitCountOverride: z
      .number()
      .int()
      .nonnegative()
      .max(10_000)
      .nullish()
      .transform((v) => v ?? null)
      .optional(),
    notes: optionalText(4_000).optional(),
    metadata: buildingMetadata.optional(),
  })
  .refine(
    (v) => Object.values(v).some((x) => x !== undefined),
    { message: 'Provide at least one field to update' },
  );
export type UpdateBuildingInput = z.infer<typeof updateBuildingInput>;

export const buildingFiltersQuery = cursorPaginationQuery.extend({
  q: z.string().trim().max(120).optional(),
  borough: boroughSchema.optional(),
  sort: z.enum(['name', 'created_at']).optional().default('name'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
});
export type BuildingFiltersQuery = z.infer<typeof buildingFiltersQuery>;

const unitNumberSchema = z.string().trim().min(1).max(40);

/** ISO `YYYY-MM-DD` date stored on the appliance summary columns
 *  (stove_last_replaced, refrigerator_last_replaced, …). Mirrors the
 *  shape compliance-schemas uses for `installedAt`. */
const optionalDate = () =>
  z
    .string()
    .date()
    .nullish()
    .transform((v) => v ?? null);

const optionalNonNegInt = (max: number) =>
  z
    .number()
    .int()
    .nonnegative()
    .max(max)
    .nullish()
    .transform((v) => v ?? null);

const optionalBool = () =>
  z
    .boolean()
    .nullish()
    .transform((v) => v ?? null);

export const createUnitInput = z.object({
  unitNumber: unitNumberSchema,
  floor: optionalText(20),
  unitType: unitTypeSchema.nullish().transform((v) => v ?? null),
  occupancyStatus: occupancyStatusSchema.optional().default('unknown'),
  bedroomCount: z.number().int().nonnegative().max(50).nullish().transform((v) => v ?? null),
  bathroomCount: z
    .number()
    .nonnegative()
    .max(50)
    .nullish()
    .transform((v) => v ?? null),
  squareFeet: z.number().int().nonnegative().max(1_000_000).nullish().transform((v) => v ?? null),
  notes: optionalText(2_000),
  metadata: buildingMetadata,
  // ---- Migration 1: utilities ----
  gasService: optionalBool(),
  electricService: optionalBool(),
  heatSource: heatSourceSchema.nullish().transform((v) => v ?? null),
  hotWaterSource: hotWaterSourceSchema.nullish().transform((v) => v ?? null),
  // ---- Migration 1: appliance summary ----
  stoveFuel: stoveFuelSchema.nullish().transform((v) => v ?? null),
  stoveLastReplaced: optionalDate(),
  refrigeratorLastReplaced: optionalDate(),
  dishwasherPresent: optionalBool(),
  dishwasherLastReplaced: optionalDate(),
  washerDryerPresent: optionalBool(),
  washerDryerLastReplaced: optionalDate(),
  windowAcCount: optionalNonNegInt(50),
  centralAc: optionalBool(),
});
export type CreateUnitInput = z.infer<typeof createUnitInput>;

/** Patch the patchable fields. Identifying fields (`id`, `org_id`,
 *  `building_id`, `unit_number`) are intentionally excluded — moving a
 *  unit to a new building or renumbering it is a separate operation. */
export const updateUnitInput = createUnitInput
  .omit({ unitNumber: true })
  .partial()
  .refine(
    (v) => Object.values(v).some((x) => x !== undefined),
    { message: 'Provide at least one field to update' },
  );
export type UpdateUnitInput = z.infer<typeof updateUnitInput>;

const bulkUnitRow = z.object({
  unitNumber: unitNumberSchema,
  floor: optionalText(20).optional(),
  unitType: unitTypeSchema.nullish().optional(),
});

export const bulkCreateUnitsInput = z
  .object({
    mode: z.enum(['range', 'list']),
    /** Range mode: "101", floor "1", unitType "apartment". */
    rangeStart: unitNumberSchema.optional(),
    rangeEnd: unitNumberSchema.optional(),
    floor: optionalText(20).optional(),
    unitType: unitTypeSchema.nullish().optional(),
    /** List mode: explicit rows (used by the Paste UI). */
    units: z.array(bulkUnitRow).max(500).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'range') {
      if (!value.rangeStart || !value.rangeEnd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rangeEnd'],
          message: 'Range mode requires rangeStart and rangeEnd',
        });
      }
    } else if (value.mode === 'list') {
      if (!value.units || value.units.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['units'],
          message: 'List mode requires at least one unit',
        });
      }
    }
  });
export type BulkCreateUnitsInput = z.infer<typeof bulkCreateUnitsInput>;

export const createBuildingContactInput = contactInput;
export type CreateBuildingContactInput = z.infer<typeof createBuildingContactInput>;

export const updateBuildingContactInput = contactInput.partial().refine(
  (v) => Object.values(v).some((x) => x !== undefined),
  { message: 'Provide at least one field to update' },
);
export type UpdateBuildingContactInput = z.infer<typeof updateBuildingContactInput>;

export const quickSearchQuery = z.object({
  q: z.string().trim().min(1).max(120),
  limit: z.coerce.number().int().min(1).max(20).default(10),
});
export type QuickSearchQuery = z.infer<typeof quickSearchQuery>;

/**
 * Parses range patterns like "101-120, 201-220" into an array of unit
 * numbers. Pure — used by the bulk-add range UI on the server and by
 * the CSV importer.
 *
 * - Each comma-separated segment is either a literal ("Studio A") or a
 *   "start-end" range. Range endpoints must share the same alphabetic
 *   prefix and parse to integers.
 * - Returns a *de-duplicated* list, preserving first-occurrence order.
 * - Caps at 1000 units per call so a typo doesn't generate a runaway list.
 */
// ---------- Building Records documents ----------

export const buildingDocumentCategorySchema = z.enum(BUILDING_DOCUMENT_CATEGORIES);

/** Certificate / filing year — keep a sane historical window. */
export const buildingDocumentYearSchema = z.coerce.number().int().min(1990).max(2100);

export const buildingDocumentListQuery = z.object({
  category: buildingDocumentCategorySchema.optional(),
  year: buildingDocumentYearSchema.optional(),
});
export type BuildingDocumentListQuery = z.infer<typeof buildingDocumentListQuery>;

export const buildingDocumentPresignInput = z.object({
  category: buildingDocumentCategorySchema,
  year: buildingDocumentYearSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Invalid content type'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  caption: optionalCaption(500),
});
export type BuildingDocumentPresignInput = z.infer<typeof buildingDocumentPresignInput>;

export const buildingDocumentConfirmInput = z.object({
  caption: optionalCaption(500).optional(),
});
export type BuildingDocumentConfirmInput = z.infer<typeof buildingDocumentConfirmInput>;

export function expandUnitsRange(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input.split(',')) {
    const segment = raw.trim();
    if (!segment) continue;
    const match = segment.match(/^([A-Za-z]*)(\d+)\s*-\s*\1(\d+)$/);
    if (match) {
      const [, prefix, startStr, endStr] = match;
      const start = Number.parseInt(startStr!, 10);
      const end = Number.parseInt(endStr!, 10);
      if (Number.isFinite(start) && Number.isFinite(end) && end >= start && end - start <= 999) {
        const pad = startStr!.length === endStr!.length ? startStr!.length : 0;
        for (let i = start; i <= end; i += 1) {
          const num = pad > 0 ? String(i).padStart(pad, '0') : String(i);
          const label = `${prefix}${num}`;
          if (!seen.has(label)) {
            seen.add(label);
            out.push(label);
            if (out.length >= 1000) return out;
          }
        }
        continue;
      }
    }
    if (!seen.has(segment)) {
      seen.add(segment);
      out.push(segment);
      if (out.length >= 1000) return out;
    }
  }
  return out;
}
